import { useState, useRef, useCallback, useEffect } from "react";
import { MapPin, BadgeCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TeacherSearchResult, MatchScoreBreakdown } from "@/services/marketplaceSearchService";
import { getTeacherMatchScore } from "@/services/marketplaceSearchService";
import { WhyThisMatch } from "@/components/match/WhyThisMatch";
import { MATCH_SCORE_UI_ENABLED } from "@/config/featureFlags";
import { RatingBadge } from "@/components/reviews/RatingBadge";
import type { MatchScore, MatchBand } from "@/types/matchScore";
import type { SearchContextSnapshot } from "@/types/searchResults";
import { useAuth } from "@/hooks/use-auth-compat";
import { currencySymbol } from "@/lib/currency";
import { OFFERING_TYPE_THEME } from "@/lib/offeringTypeTheme";
import { SubscriptionTierBadge } from "@/components/teacher/SubscriptionTierBadge";

interface TeacherCardProps {
  teacher: TeacherSearchResult;
  onClick?: () => void;
  className?: string;
  /** Smaller card (shorter photo, tighter padding, fewer rows) — used on the dashboard. */
  compact?: boolean;
  searchContext?: SearchContextSnapshot | null;
}

const MODE_LABELS: Record<string, string> = {
  ONE_TO_ONE: "1-on-1",
  GROUP: "Group",
  CRASH_COURSE: "Crash Course",
};

const MODE_ICONS: Record<string, React.ReactNode> = {
  ONE_TO_ONE: <OFFERING_TYPE_THEME.ONE_TO_ONE.icon className="w-3 h-3" />,
  GROUP: <OFFERING_TYPE_THEME.GROUP.icon className="w-3 h-3" />,
  CRASH_COURSE: <OFFERING_TYPE_THEME.CRASH_COURSE.icon className="w-3 h-3" />,
};

const AVATAR_GRADIENTS = [
  "from-violet-500 to-indigo-600",
  "from-sky-500 to-amber-600",
  "from-emerald-500 to-amber-600",
  "from-orange-500 to-rose-500",
  "from-pink-500 to-fuchsia-600",
];

const SUBJECT_COLORS = [
  { bg: "bg-violet-50", text: "text-violet-700", dot: "bg-violet-400" },
  { bg: "bg-sky-50",    text: "text-sky-700",    dot: "bg-sky-400" },
  { bg: "bg-emerald-50",text: "text-emerald-700",dot: "bg-emerald-400" },
  { bg: "bg-amber-50",  text: "text-amber-700",  dot: "bg-amber-400" },
  { bg: "bg-rose-50",   text: "text-rose-700",   dot: "bg-rose-400" },
  { bg: "bg-amber-50",   text: "text-amber-700",   dot: "bg-amber-400" },
];

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function toMatchScore(b: MatchScoreBreakdown): MatchScore {
  return {
    total: b.total,
    maxTotal: b.maxTotal,
    band: b.band as MatchBand,
    dimensions: b.dimensions.map(d => ({
      key: d.key,
      label: d.label,
      points: d.points,
      max: d.max,
      result: d.result,
      detail: d.detail,
    })),
    nonScored: (b.nonScored ?? []).map(d => ({
      key: d.key,
      label: d.label,
      points: d.points,
      max: d.max,
      result: d.result,
      detail: d.detail,
    })),
  };
}

function getGradient(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
}

function formatPrice(min: number | null, max: number | null, currency: string | null | undefined): string | null {
  const sym = currencySymbol(currency);
  if (min == null && max == null) return null;
  if (min == null) return `${sym}${max}`;
  if (max == null || min === max) return `${sym}${min}`;
  return `${sym}${min}–${max}`;
}

const BAND_STYLES: Record<string, { label: string; text: string; bg: string; bar: string }> = {
  STRONG: { label: 'Strong match', text: 'text-emerald-700', bg: 'bg-emerald-50', bar: 'bg-emerald-500' },
  GOOD:   { label: 'Good match',   text: 'text-amber-700',   bg: 'bg-amber-50',   bar: 'bg-amber-500' },
  PARTIAL:{ label: 'Partial match',text: 'text-slate-600',   bg: 'bg-slate-100',  bar: 'bg-slate-400' },
  WEAK:   { label: 'Low match',    text: 'text-slate-500',   bg: 'bg-slate-100',  bar: 'bg-slate-300' },
};

export function TeacherCard({ teacher, onClick, className, compact, searchContext }: TeacherCardProps) {
  const { getToken } = useAuth();
  // SCRUM-365: the card's own backend currency is the only source; null = no symbol.
  const priceLabel = formatPrice(teacher.minPrice, teacher.maxPrice, teacher.currency);
  const bandStyle = teacher.matchScore != null && teacher.matchBand
    ? (BAND_STYLES[teacher.matchBand] ?? null)
    : null;
  const showScore = MATCH_SCORE_UI_ENABLED && bandStyle != null && teacher.matchScore != null;
  const [breakdown, setBreakdown] = useState<MatchScoreBreakdown | null>(null);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  const fetchedHandle = useRef<string | null>(null);

  // Reset cached handle when search context changes so the next hover re-fetches
  // with the updated subject/gradeBand/curriculum from the new search results.
  useEffect(() => {
    fetchedHandle.current = null;
  }, [searchContext]);

  const handleScoreEnter = useCallback(async () => {
    if (!teacher.handle) return;
    setShowBreakdown(true);
    if (fetchedHandle.current === teacher.handle) return;
    setLoadingBreakdown(true);
    try {
      const token = await getToken();
      if (!token) return;
      const data = await getTeacherMatchScore(teacher.handle, token, searchContext);
      setBreakdown(data);
      fetchedHandle.current = teacher.handle;
    } catch {
      // silently ignore — breakdown stays null
    } finally {
      setLoadingBreakdown(false);
    }
  }, [teacher.handle, getToken, searchContext]);

  const handleScoreLeave = useCallback(() => {
    setShowBreakdown(false);
  }, []);

  const visibleSubjects = teacher.subjects.slice(0, 3);
  const extraSubjects = teacher.subjects.length - 3;
  const gradient = getGradient(teacher.fullName);

  return (
    <div
      onClick={onClick}
      className={cn(
        "group relative bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl hover:shadow-slate-200/60 hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col",
        showBreakdown && "z-10",
        className
      )}
    >
      {/* Full-width photo */}
      <div className={cn("relative overflow-hidden", compact ? "h-40" : "aspect-[4/3]")}>
        {teacher.profilePictureUrl ? (
          <img
            src={teacher.profilePictureUrl}
            alt={teacher.fullName}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className={cn("w-full h-full bg-gradient-to-br flex items-center justify-center", gradient)}>
            <span className={cn("text-white font-bold", compact ? "text-3xl" : "text-5xl")}>{getInitials(teacher.fullName)}</span>
          </div>
        )}

        {/* Gradient overlay for bottom legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />

        {/* Free trial pill — top left */}
        {teacher.trialOffered && (
          <span className="absolute top-3 left-3 text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-emerald-500 text-white shadow">
            Free trial
          </span>
        )}

        {/* Price — top right */}
        {priceLabel && (
          <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-lg px-2.5 py-1 shadow">
            <span className="text-xs font-bold text-slate-900">{priceLabel}</span>
            <span className="text-xs text-slate-500">/session</span>
          </div>
        )}

        {/* Name + handle overlaid on photo bottom */}
        <div className="absolute bottom-0 left-0 right-0 px-4 pb-3">
          <div className="flex items-end justify-between gap-2">
            <div className="min-w-0">
              <h3 className={cn("font-extrabold text-white leading-tight truncate drop-shadow-md", compact ? "text-base" : "text-lg")}>
                {teacher.fullName}
              </h3>
              {teacher.handle && (
                <p className="text-xs text-white/75 mt-0.5">@{teacher.handle}</p>
              )}
              <SubscriptionTierBadge tier={teacher.teacherTier} className="mt-1" />
            </div>
            {/* Verified + rating */}
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <div className="w-6 h-6 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <BadgeCheck className="w-4 h-4 text-white" />
              </div>
              <RatingBadge ratingAvg={teacher.ratingAvg} ratingCount={teacher.ratingCount} />
            </div>
          </div>
        </div>
      </div>

      <div className={cn("flex flex-col flex-1", compact ? "p-4 gap-2.5" : "p-4 gap-3")}>

        {/* Match score bar — shown for all bands when student is logged in; hover reveals breakdown */}
        {showScore && bandStyle && (
          <div
            className="relative"
            onMouseEnter={handleScoreEnter}
            onMouseLeave={handleScoreLeave}
          >
            <div className={cn('flex items-center gap-2 rounded-lg px-2.5 py-1.5 cursor-pointer transition-opacity', bandStyle.bg)}>
              <div className="flex-1 h-1.5 rounded-full bg-white/60 overflow-hidden min-w-[36px]">
                <div
                  className={cn('h-full rounded-full', bandStyle.bar)}
                  style={{ width: `${Math.round((teacher.matchScore! / 100) * 100)}%` }}
                />
              </div>
              <span className={cn('text-xs font-semibold shrink-0', bandStyle.text)}>
                {bandStyle.label}
              </span>
              <span className={cn('text-xs tabular-nums shrink-0', bandStyle.text)}>
                {teacher.matchScore}/100
              </span>
            </div>
            {showBreakdown && (
              <div
                className="absolute left-0 right-0 top-full mt-1.5 z-50"
                onClick={e => e.stopPropagation()}
              >
                {loadingBreakdown ? (
                  <div className="text-xs text-slate-400 text-center py-2 bg-white border border-slate-200 rounded-lg shadow-lg">Loading breakdown…</div>
                ) : breakdown ? (
                  <div className="shadow-xl rounded-xl overflow-hidden">
                    <WhyThisMatch score={toMatchScore(breakdown)} />
                  </div>
                ) : null}
              </div>
            )}
          </div>
        )}

        {/* Subject tags */}
        {visibleSubjects.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {visibleSubjects.map((s, i) => {
              const c = SUBJECT_COLORS[i % SUBJECT_COLORS.length];
              return (
                <span
                  key={s.id}
                  className={cn(
                    "inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full",
                    c.bg, c.text
                  )}
                >
                  <span className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0", c.dot)} />
                  {s.name}
                </span>
              );
            })}
            {extraSubjects > 0 && (
              <span className="inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-500">
                +{extraSubjects}
              </span>
            )}
          </div>
        )}

        {/* Mode badges */}
        {!compact && teacher.offeringTypes.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {teacher.offeringTypes.map((type) => (
              <span
                key={type}
                className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600"
              >
                {MODE_ICONS[type]}
                {MODE_LABELS[type] ?? type}
              </span>
            ))}
          </div>
        )}

        {/* Location */}
        {(teacher.locationDisplayName || teacher.distanceKm != null) && (
          <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
            <MapPin className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" />
            <span className="truncate">
              {teacher.locationDisplayName}
              {teacher.distanceKm != null && ` · ${teacher.distanceKm.toFixed(1)} km`}
            </span>
          </div>
        )}

        {/* CTA */}
        <div className="mt-auto">
          <button
            onClick={(e) => { e.stopPropagation(); onClick?.(); }}
            className={cn(
              "w-full rounded-xl font-bold transition-all duration-200",
              compact ? "py-2.5 text-xs" : "py-2.5 text-sm",
              "bg-gradient-to-r from-[hsl(210,60%,18%)] to-[hsl(210,60%,25%)] text-white",
              "hover:shadow-md hover:shadow-[hsl(210,60%,18%)]/30 hover:brightness-110",
              "active:scale-[0.98]"
            )}
          >
            View Profile
          </button>
        </div>
      </div>
    </div>
  );
}
